import React, { useState, useEffect, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import Button from "../Components/Button";
import ReportsLayout from "./ReportsLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCheck, faBan } from "@fortawesome/free-solid-svg-icons";
import { FilterContext } from "../FilterContextProvider";
import ReactToPrint from "react-to-print";
import { useParams } from "react-router-dom";
import useApi from "../Api/Api";
import useEventSales from "../hooks/useEventSales";
import { exportToCSV } from "../Utility/Csv";
import { DateTime } from "luxon";

const SalesSheet = () => {
  const { filterData } = useContext(FilterContext);
  const printRef = useRef();
  const api = useApi();
  const [hallRates, setHallRates] = useState([]);
  const [eventFees, setEventFees] = useState([]);
  const params = useParams();

  const { eventSales } = useEventSales(params.eventId);

  hallRates.sort((a) => (a.name === "Clothing" ? 1 : -1)).sort((a) => (a.name === "Music" ? 1 : -1));

  const [event, setEvent] = useState({
    date: "",
    id: params.eventId,
  });

  const addEventTaxToProductValue = ({ product, value }) => {
    if (!event.new_tax_formula) {
      return value;
    }

    const productHallRate = hallRates.find((rate) => rate.name === product.product_category);
    if (!productHallRate || !productHallRate.taxed) {
      return value;
    }

    const parsedTaxRate = parseFloat(productHallRate.tax_rate_override || event.tax_rate);
    const taxPercent = parsedTaxRate >= 1 ? parsedTaxRate / 100 : parsedTaxRate;
    return value * (1 + taxPercent);
  };

  useEffect(() => {
    api.get(`/events/${params.eventId}`).then((r) => {
      setEvent(r.data);
    });
  }, [api, params.eventId]);

  useEffect(() => {
    api.get(`/event_category_setups?event_id=${params.eventId}`).then((r) => {
      setHallRates(r.data);
    });
  }, [api, params.eventId]);

  useEffect(() => {
    api.get(`/event_fees?event_id=${params.eventId}`).then((r) => {
      setEventFees(r.data);
    });
  }, [api, params.eventId]);

  const grossSales = eventSales.reduce((a, b) => {
    return a + b.gross_sales;
  }, 0);

  const grossAndTaxExclusive = grossSales + event.tax_net;

  const discountSales = eventSales.reduce((a, b) => {
    return a + b.discount_sales;
  }, 0);

  const totalAdd3s = eventSales.reduce((a, b) => {
    return a + b.add_3;
  }, 0);

  const totalAdd4s = eventSales.reduce((a, b) => {
    return a + b.add_4;
  }, 0);

  const pageBreak = {
    display: "block",
    pageBreakBefore: "always",
  };

  // CSV export data - excludes Cost field
  const event_sales = eventSales.map((sale) => ({
    Product: sale.product_name,
    Size: sale.product_size,
    Count_in: sale.count_in,
    Add_1: sale.add,
    Add_2: sale.add_2,
    Add_3: sale.add_3,
    Add_4: sale.add_4,
    Count_out: sale.count_out,
    Discount_quantity: sale.discount_quantity,
    Damage_quantity: sale.damage_quantity,
    Band_comp: sale.band_comp_quantity,
    TM_comp: sale.ctm_comp,
    Price: sale.price,
    Discount_price: sale.discount_price,
    Sold: sale.sold,
    Gross: sale.sold * sale.price,
    Percent_of_sales: ((sale.sold * sale.price) / grossSales) * 100,
  }));

  const renderCategoryHallRates = (each) => {
    if (each) {
      // Use the stored gross_hall value from the database
      each.hall_fee = parseFloat(each.gross_hall || 0);
    }
    const taxRate = each?.tax_rate_override || event.tax_rate;
    return (
      <div key={each.id} className="flex w-3/4 text-right justify-between my-2 border-b border-black">
        <div className="w-1/6 text-left pr-2">{each?.name}</div>
        <div className="w-1/6 pr-2">
          {each?.gross_sales
            ? each?.gross_sales?.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })
            : ""}
        </div>
        <div className="w-1/6 pr-2">
          {taxRate
            ? (taxRate * 100)?.toLocaleString(undefined, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })
            : 0}
          %
        </div>
        <div className="w-1/6 pr-2">
          {each?.gross_tax
            ? each.gross_tax?.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })
            : ""}
        </div>
        <div className="w-1/6 pr-2">
          {each?.hall_rate
            ? (each?.hall_rate * 100)?.toLocaleString(undefined, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })
            : 0}
          %
        </div>
        <div className="w-1/6 pr-2">
          {each?.hall_fee
            ? each?.hall_fee?.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })
            : ""}
        </div>
        <div className="w-1/8 pr-5">{each?.taxed && <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>}</div>
      </div>
    );
  };

  const renderEventFees = (each) => {
    return (
      <div key={each.id} className="w-3/4 flex justify-between mt-1 border-b border-black">
        <div className="w-1/2">{each?.description}</div>
        <div className="w-1/4">{each?.amount}</div>
        <div className="w-1/4 text-center">
          {each?.before_hall_fee && <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>}
        </div>
      </div>
    );
  };


  return (
    <ReportsLayout>
      <div className="mt-8 mx-8 py-10 px-12 bg-white rounded-lg border">
        <div className="flex justify-end text-sm">
          <ReactToPrint
            trigger={() => <Button className="button" text="Print"></Button>}
            content={() => printRef.current}
          />
          <Button
            className="button button-white ml-2"
            text="Export"
            onClick={() => exportToCSV(event_sales, `${filterData.artist_name}_sales_sheet`)}
          ></Button>
        </div>
        <div className="-mt-5 font-medium -mx-2 text-black text-sm">
          <Link to="/reports/events">
            <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to events list
          </Link>
        </div>
        <div ref={printRef} className="mt-8 mx-8 text-black">
          <div className="mb-2 p-2 border-2 border-black max-w-max">
            <div className="text-xl font-extrabold">TM Sales Sheet Report</div>
            <div className="flex">
              <div className="flex">
                <div>
                  <div>Artist Name:</div>
                  <div>Event Name:</div>
                  <div>Event Date:</div>
                  <div>Location:</div>
                </div>
                <div className="ml-2">
                  <div>{filterData.artist_name}</div>
                  <div>{event.name}</div>
                  <div>
                    {event.date?.slice(5, 7)}/{event.date?.slice(8, 10)}/{event.date?.slice(0, 4)}
                  </div>
                  <div>
                    {event.city}, {event.state}
                  </div>
                </div>
              </div>
              <div className="flex ml-32">
                <div>
                  <div>Gross Sales:</div>
                  <div>Discount Sales:</div>
                  <div>Attendance:</div>
                  <div>Per Cap:</div>
                </div>
                <div className="ml-2 text-right">
                  <div>
                    {(event.new_tax_formula ? grossAndTaxExclusive : grossSales)?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {discountSales?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>{event.attendance ? event.attendance?.toLocaleString() : 0}</div>
                  <div>
                    {event.attendance
                      ? (event.gross_sales / event.attendance)?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })
                      : 0?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <table>
            <thead className="text-xs font-light">
              <tr className="border-b-2 border-black h-11 text-right">
                <td className="w-1/5 text-left">PRODUCT</td>
                <td className="w-1/25 text-left">SIZE</td>
                <td className="w-1/25">IN</td>
                <td className="w-1/25">ADD 1</td>
                <td className="w-1/25">ADD 2</td>
                {totalAdd3s > 0 && <td className="w-1/25">ADD 3</td>}
                {totalAdd4s > 0 && <td className="w-1/25">ADD 4</td>}
                <td className="w-1/25">OUT</td>
                <td className="w-1/25">DISC.</td>
                <td className="w-1/25">BAD</td>
                <td className="w-1/25">BAND COMP</td>
                <td className="w-1/25">TM COMP</td>
                <td className="w-1/12">RETAIL PRICE</td>
                <td className="w-1/12">DISCOUNT PRICE</td>
                <td className="w-1/25">SOLD</td>
                <td className="w-1/12">GROSS</td>
                <td className="w-1/25">% SALES</td>
              </tr>
            </thead>
            <tbody>
              {eventSales
                .sort((a, b) => {
                  if (a.category?.index !== b.category?.index) {
                    return a.category?.index > b.category?.index ? 1 : -1;
                  }
                  if (a.product_name !== b.product_name) {
                    return a.product_name?.localeCompare(b.product_name);
                  }

                  a.sortSize = a.size?.index ?? Infinity;
                  b.sortSize = b.size?.index ?? Infinity;

                  if (a.sortSize !== b.sortSize) {
                    return a.sortSize > b.sortSize ? 1 : -1;
                  }

                  return DateTime.fromISO(a.product?.created_at) > DateTime.fromISO(b.product?.created_at) ? 1 : -1;
                })
                .map((productToRender, i) => {
                  return (
                    <tr className="text-sm text-right border-b border-black" key={productToRender.id}>
                      <td className="text-left">{productToRender.product_name}</td>
                      <td className="text-left">{productToRender.product_size}</td>
                      <td id={`count_in_${i}`}>{productToRender.count_in}</td>
                      <td id={`add_${i}`}>{productToRender.add}</td>
                      <td id={`add_2_${i}`}>{productToRender.add_2}</td>
                      {totalAdd3s > 0 && <td id={`add_3_${i}`}>{productToRender.add_3}</td>}
                      {totalAdd4s > 0 && <td id={`add_4_${i}`}>{productToRender.add_4}</td>}
                      <td id={`count_out_${i}`}>{productToRender.count_out}</td>
                      <td id={`discount_quantity_${i}`}>{productToRender.discount_quantity}</td>
                      <td id={`damage_quantity_${i}`}>{productToRender.damage_quantity}</td>
                      <td id={`band_comp_quantity_${i}`}>{productToRender.band_comp_quantity}</td>
                      <td id={`ctm_comp_${i}`}>{productToRender.ctm_comp}</td>

                      <td id={`price_${i}`}>
                        {addEventTaxToProductValue({
                          product: productToRender,
                          value: parseFloat(productToRender.price),
                        })?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td id={`discount_price_${i}`}>
                        {parseFloat(productToRender.discount_price)?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td id={`sold${i}`} className={productToRender.sold < 0 ? "bg-red-300" : ""}>
                        {productToRender.sold}
                      </td>
                      <td id={`gross${i}`} className={productToRender.gross_sales < 0 ? "bg-red-300" : ""}>
                        {addEventTaxToProductValue({
                          product: productToRender,
                          value: parseFloat(productToRender.gross_sales),
                        })?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td className={productToRender.discount_quantity < 0 ? "bg-red-300" : ""}>
                        {(parseFloat(productToRender.gross_sales) / grossSales)?.toLocaleString("en-US", {
                          style: "percent",
                        })}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          <div style={pageBreak}>
            <div className="mt-20 tracking-normal font-medium text-lg italic">Sale Categories</div>
            <div>
              <div className="w-3/4 text-right font-medium flex justify-between underline">
                <div className="w-1/6 text-left">Category</div>
                <div className="w-1/6">Gross Sales</div>
                <div className="w-1/6">Tax Rate</div>
                <div className="w-1/6">Sales Tax</div>
                <div className="w-1/6">Hall Rate</div>
                <div className="w-1/6">Hall Fee</div>
                <div className="w-1/8">Taxable</div>
              </div>
              {hallRates.map((each) => renderCategoryHallRates(each))}
            </div>
            <div className="mt-8 tracking-normal font-medium text-lg italic">Event Fees</div>
            <div>
              <div className="w-3/4 font-medium flex underline">
                <div className="w-1/2">Fee Description</div>
                <div className="w-1/4">Amount</div>
                <div className="w-1/4 text-center">Before Hall Fee</div>
              </div>
              {eventFees.map((each) => renderEventFees(each))}
            </div>
            <div className="flex mt-16 min-w-max text-right">
              <div className="flex">
                <div className="italic">
                  <div>Gross Sales:</div>
                  <div>Discount Sales:</div>
                  <div>Taxable Gross:</div>
                  <div>Tax:</div>
                  <div>Hall Fee:</div>
                  <div>CC Fee:</div>
                  <div>Event Fees:</div>
                  <div>Total to Artist:</div>
                  <div>Total Due Venue:</div>
                </div>
                <div className="ml-8">
                  <div>
                    {grossSales?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {discountSales?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {event.taxable_gross?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {event.tax_net?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {event.hall_net?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {event.cc_net?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {event.fee_net?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {event.total_to_artist?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {event.total_to_venue?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                </div>
              </div>
              <div className="flex mt-12">
                <div className="ml-4">@</div>
                <div className="ml-4">
                  {(event.tax_rate * 100)?.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  })}
                  %
                </div>
              </div>
              <div className="flex ml-12">
                <div className="italic">
                  <div>Credit Card Sales:</div>
                  <div>Credit Card Rate:</div>
                  <div>Tax Kept and Remitted By Venue:</div>
                  {event.vendor === "Venue" && (
                    <>
                      <div>Cash Paid to Aritst:</div>
                      <div>Check Due to Artist:</div>
                    </>
                  )}
                  {event.vendor === "Artist" && (
                    <>
                      <div>Cash Paid to Venue:</div>
                      <div>Check Due to Venue:</div>
                    </>
                  )}
                  <div>Comments:</div>
                </div>
                <div className="ml-8">
                  <div>
                    {event.cc_sales?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {(event.cc_rate * 100)?.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })}
                    %
                  </div>
                  <div>
                    {event.tax_kept_by_venue ? (
                      <FontAwesomeIcon icon={faCheck}></FontAwesomeIcon>
                    ) : (
                      <FontAwesomeIcon icon={faBan}></FontAwesomeIcon>
                    )}
                  </div>
                  {event.vendor === "Venue" && (
                    <>
                      <div>
                        {event.cash_paid_to_artist
                          ? event.cash_paid_to_artist?.toLocaleString(undefined, {
                              style: "currency",
                              currency: "USD",
                            })
                          : 0?.toLocaleString(undefined, {
                              style: "currency",
                              currency: "USD",
                            })}
                      </div>
                      <div>
                        {event.check_due_to_artist
                          ? event.check_due_to_artist?.toLocaleString(undefined, {
                              style: "currency",
                              currency: "USD",
                            })
                          : 0?.toLocaleString(undefined, {
                              style: "currency",
                              currency: "USD",
                            })}
                      </div>
                    </>
                  )}
                  {event.vendor === "Artist" && (
                    <>
                      <div>
                        {event.cash_paid_to_venue
                          ? event.cash_paid_to_venue?.toLocaleString(undefined, {
                              style: "currency",
                              currency: "USD",
                            })
                          : 0?.toLocaleString(undefined, {
                              style: "currency",
                              currency: "USD",
                            })}
                      </div>
                      <div>
                        {event.check_due_to_venue
                          ? event.check_due_to_venue?.toLocaleString(undefined, {
                              style: "currency",
                              currency: "USD",
                            })
                          : 0?.toLocaleString(undefined, {
                              style: "currency",
                              currency: "USD",
                            })}
                      </div>
                    </>
                  )}
                  <div>{event.comment}</div>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <img src="/ctm_logo.png" alt="ctm_logo" className="mt-12 max-w-80 mb-4" />
            </div>
          </div>
        </div>
      </div>
    </ReportsLayout>
  );
};

export default SalesSheet;
